import { SyntheticEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DatePicker,
  DateTools,
  Input,
  Patterns,
  Select,
  NumberInput,
  Divider,
  RadioList,
  Utils,
} from '@treezor/design-system';

import { TCountryCode } from '@/features/user/store/user.model';
import { useMailInput } from '@/features/user/hooks/createUser/useMailInput';
import { SirenInput } from '@/features/user/components/CreateUser/SirenInput';
import { Contact } from '@/features/user/components/CreateUser/Contact';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { CreateUserFooter } from '@/features/user/components/CreateUser/layout/CreateUserFooter';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import {
  ContentLayout,
  CustomColumns,
  Section,
  TwoColumns,
} from '@/features/user/components/CreateUser/layout/ContentLayout';
import { useCreateBusinessUser } from '@/features/user/hooks/createUser/useCreateBusinessUser';

import { UserTools } from '@/utils/tools/UserTools';
import { ICreateUserPartProps } from '@/features/user/store/createUser.types';

export function CreateUserBusinessInformation({ stepsLength }: ICreateUserPartProps) {
  const { t } = useTranslation();

  const {
    distributionCountry,
    handleSubmit,
    watches,
    user,
    updateUser,
    register,
    touchedFields,
    inputMaxLength,
    hasValidField,
    handleInputChange,
    handleValueChange,
    handleDateChange,
    hasInvalidInputLength,
    language,
    handlePrefillData,
  } = useCreateBusinessUser({
    email: '',
    distributionCountry: undefined,
    address1: '',
    address2: '',
    address3: '',
    postcode: '',
    city: '',
    phone: '',
    mobile: '',
    legalName: '',
    legalRegistrationNumber: '',
    legalRegistrationDate: '',
    legalTvaNumber: '',
    legalShareCapital: undefined,
    country: undefined,
    legalSectorType: 'NAF',
    legalSector: '',
    legalForm: '',
  });

  const { countryCodeOptions, legalFormOptions, legalSectorTypeOptions } = useFieldOptions();

  const hasDistributionCountry = distributionCountry?.length > 0;

  const emailWatch = watches['email'];
  const distributionCountryWatch = watches['distributionCountry'];
  const phoneWatch = watches['phone'];
  const mobileWatch = watches['mobile'];
  const address1Watch = watches['address1'];
  const address2Watch = watches['address2'];
  const address3Watch = watches['address3'];
  const legalRegistrationNumberWatch = watches['legalRegistrationNumber'];
  const legalRegistrationDateWatch = watches['legalRegistrationDate'];
  const selectedlegalRegistrationDate = DateTools.localStringToDate(legalRegistrationDateWatch, language);
  const countryWatch = watches['country'];
  const legalSectorTypeWatch = watches['legalSectorType'];
  const legalSectorWatch = watches['legalSector'];

  const {
    iconStatus,
    error: emailError,
    handleAsyncBlur: handleEmailBlur,
  } = useMailInput({ initialValue: emailWatch });

  const hasEnteredInformation = hasValidField([
    'legalRegistrationNumber',
    'legalName',
    'legalSector',
    'legalTvaNumber',
    'address1',
    'phone',
    'city',
    'country',
    'postcode',
  ]);

  const isFormDisabled = useMemo(
    () =>
      !emailWatch ||
      !!emailError ||
      (hasDistributionCountry && !distributionCountryWatch) ||
      hasInvalidInputLength(['address1', 'address2', 'address3']) ||
      (!!mobileWatch && !Patterns.phone.test(mobileWatch)) ||
      (!!phoneWatch && !Patterns.phone.test(phoneWatch)) ||
      !legalRegistrationNumberWatch ||
      !UserTools.validateIntLegalNumber(legalRegistrationNumberWatch) ||
      (!!legalSectorWatch && !Utils.validateLegalSector(legalSectorWatch, legalSectorTypeWatch)),
    [
      emailWatch,
      emailError,
      hasDistributionCountry,
      distributionCountryWatch,
      hasInvalidInputLength,
      mobileWatch,
      phoneWatch,
      legalRegistrationNumberWatch,
      legalSectorTypeWatch,
      legalSectorWatch,
    ],
  );

  return (
    <div className="flex flex-col gap-7">
      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.mainInformation')}>
        <div className="flex flex-col gap-7">
          <Section>
            <Input
              type="text"
              icon={{
                left: 'mail',
                right: iconStatus,
              }}
              label={t('pages.dashboard.createUser.form.email')}
              required
              error={emailError}
              {...register('email', { pattern: Patterns.email })}
              onBlur={handleEmailBlur}
            />

            {hasDistributionCountry && (
              <Select
                label={t('pages.currentProfiles.profile.informations.distributionCountry')}
                required
                withSearch
                options={countryCodeOptions.filter(({ value }) => distributionCountry.includes(value))}
                defaultValue={user?.distributionCountry}
                noOptionsComponent={<CreateUserEmptyComponent />}
                onChange={(value: string) => handleValueChange(value, 'distributionCountry')}
              />
            )}
          </Section>

          <Section title={t('pages.dashboard.createUser.business.sections.legalInformation')}>
            <SirenInput
              value={legalRegistrationNumberWatch}
              hasEntered={hasEnteredInformation}
              label={t('pages.dashboard.createUser.form.sirenLegalRegistrationNumber')}
              required
              handlePrefillData={handlePrefillData}
              {...register('legalRegistrationNumber')}
            >
              <Input
                {...register('legalName')}
                type="text"
                label={t('pages.dashboard.createUser.form.legalName')}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleInputChange(event, 'legalName')
                }
              />
              <CustomColumns columns={[1, 2]}>
                <DatePicker
                  selectedDate={selectedlegalRegistrationDate}
                  label={t('pages.dashboard.createUser.form.legalRegistrationDate')}
                  todayButtonLabel={t('pages.dashboard.createUser.form.actions.today')}
                  clearButtonLabel={t('pages.dashboard.createUser.form.actions.clear')}
                  onChangeDate={(value) => handleDateChange(value, 'legalRegistrationDate')}
                  language={language}
                />
                <Select
                  label={t('pages.dashboard.createUser.form.legalForm')}
                  options={legalFormOptions(distributionCountryWatch)}
                  withSearch
                  defaultValue={user?.legalForm}
                  noOptionsComponent={<CreateUserEmptyComponent />}
                  onChange={(value: string) => handleValueChange(value, 'legalForm')}
                  showDelete
                />
              </CustomColumns>

              <TwoColumns>
                <Input
                  type="text"
                  {...register('legalTvaNumber')}
                  label={t('pages.dashboard.createUser.form.legalTvaNumber')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    handleInputChange(event, 'legalTvaNumber');
                  }}
                />

                <NumberInput
                  name="legalShareCapital"
                  defaultValue={user?.legalShareCapital}
                  label={t('pages.dashboard.createUser.form.legalShareCapital')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'legalShareCapital')
                  }
                  unit={
                    <div className="flex gap-3 items-center h-full">
                      <Divider direction="vertical" />
                      <div className="text-xs w-8 min-w-8 text-primary">EUR (€)</div>
                    </div>
                  }
                />
              </TwoColumns>
              <Section>
                <h6 className="typo-h6-bold text-secondary">
                  {t('pages.dashboard.createUser.business.sections.businessAddress')}
                </h6>

                <Select
                  label={t('pages.currentProfiles.profile.contact.country')}
                  options={countryCodeOptions}
                  withSearch
                  defaultValue={countryWatch}
                  onChange={(value: string) => {
                    handleValueChange(value, 'country');
                    updateUser({ ...user, country: value as TCountryCode });
                  }}
                  noOptionsComponent={<CreateUserEmptyComponent />}
                  showDelete
                />

                <TwoColumns>
                  <Input
                    value={address1Watch}
                    type="text"
                    label={t('pages.dashboard.createUser.form.address1')}
                    {...register('address1')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'address1')
                    }
                    {...inputMaxLength(address1Watch, 35)}
                  />

                  <Input
                    value={address2Watch}
                    type="text"
                    label={t('pages.dashboard.createUser.form.address2')}
                    {...register('address2')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'address2')
                    }
                    {...inputMaxLength(address2Watch, 35)}
                  />
                </TwoColumns>

                <Input
                  value={address3Watch}
                  type="text"
                  label={t('pages.dashboard.createUser.form.address3')}
                  {...register('address3')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'address3')
                  }
                  {...inputMaxLength(address3Watch, 35)}
                />

                <TwoColumns>
                  <Input
                    type="text"
                    label={t('pages.currentProfiles.profile.contact.postcode')}
                    {...register('postcode')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'postcode')
                    }
                  />

                  <Input
                    type="text"
                    label={t('pages.currentProfiles.profile.contact.city')}
                    {...register('city')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'city')
                    }
                  />
                </TwoColumns>
              </Section>

              <Section>
                <h6 className="typo-h6-bold text-secondary">
                  {t('pages.dashboard.createUser.business.sections.companyActivities')}
                </h6>

                <TwoColumns>
                  <RadioList
                    label={t('pages.dashboard.createUser.form.legalSectorType')}
                    options={legalSectorTypeOptions}
                    value={user?.legalSectorType || legalSectorTypeWatch}
                    onChange={(value: string) => handleValueChange(value, 'legalSectorType')}
                    className="pt-2 gap-3"
                  />
                  <Input
                    type="text"
                    label={t('pages.dashboard.createUser.form.legalSectorNafApe')}
                    {...register('legalSector')}
                    error={
                      legalSectorWatch === '' || Utils.validateLegalSector(legalSectorWatch, legalSectorTypeWatch)
                        ? undefined
                        : legalSectorTypeWatch === 'NACE'
                          ? t('pages.dashboard.createUser.form.rules.incorrectNaceLegalSector')
                          : t('pages.dashboard.createUser.form.rules.incorrectLegalSector')
                    }
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'legalSector')
                    }
                    defaultValue={user?.legalSector}
                  />
                </TwoColumns>
              </Section>
            </SirenInput>
          </Section>
        </div>
      </ContentLayout>

      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.contact')} hasDivider={false}>
        <TwoColumns>
          <Contact
            label={t('pages.dashboard.createUser.form.phone')}
            value={phoneWatch}
            initialValue={user?.phone}
            initialCountry={!touchedFields?.['phone'] && !phoneWatch && distributionCountryWatch}
            handleValueChange={handleValueChange}
            {...register('phone')}
          />
          <Contact
            label={t('pages.dashboard.createUser.form.mobile')}
            value={mobileWatch}
            initialValue={user?.mobile}
            initialCountry={!touchedFields?.['mobile'] && !mobileWatch && distributionCountryWatch}
            handleValueChange={handleValueChange}
            {...register('mobile')}
          />
        </TwoColumns>
      </ContentLayout>
      <CreateUserFooter isNextDisabled={isFormDisabled} onNext={handleSubmit} stepsLength={stepsLength} />
    </div>
  );
}
